import { useQueryClient } from "@tanstack/react-query"

import { invalidateDocumentProject, invalidateDocumentsList } from "@l2r-front/l2r-app-doc"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { l2rApiAxiosInstance as axiosInstance } from "@l2r-front/l2r-query"
import { invalidateCampaigns, invalidateCampaignsList } from "@l2r-front/l2r-shared-campaign"

import { horizontalSigningQueryKeys } from "../queries/horizontalSigning/queryKeys"

import { useHorizontalSigningMutation } from "./useHorizontalSigningMutation"

export function useHorizontalSignUpdate(
    signId,
    queryParameters,
    config = {},
) {
    const url = `horizontal_signs/${signId}/`
    const queryClient = useQueryClient()

    return useHorizontalSigningMutation(
        url,
        axiosInstance.patch,
        {
            ...queryParameters,
        },
        {
            ...config,
            onSuccess: (data) => {
                queryClient.invalidateQueries({ queryKey: horizontalSigningQueryKeys.feature(signId) })
                queryClient.invalidateQueries({ queryKey: horizontalSigningQueryKeys.project() })
                queryClient.invalidateQueries({ queryKey: horizontalSigningQueryKeys.layer() })
                queryClient.invalidateQueries({ queryKey: horizontalSigningQueryKeys.stats_categories() })
                queryClient.invalidateQueries({ queryKey: horizontalSigningQueryKeys.stats_conditions() })
                config.onSuccess?.(data)
            },
        },
    )
}

export function useHorizontalSignUpdateDocument(
    signId,
    queryParameters,
    config = {},
) {
    const url = `horizontal_signs/${signId}/`
    const queryClient = useQueryClient()
    const { selectedNetwork } = useNetworksStateContext()

    const axiosFn = (url, values) => {
        const formData = new FormData()
        Object.entries(values).forEach(([key, value]) => {
            if (key === "document") {
                Object.entries(values[key]).forEach(([docKey, docValue]) => {
                    if (Array.isArray(docValue)) {
                        formData.append(`${key}.${docKey}`, JSON.stringify(docValue))
                    } else {
                        formData.append(`${key}.${docKey}`, docValue)
                    }
                })
            } else {
                formData.append(key, value)
            }
        })

        const headers = values.document ? { "Content-Type": "multipart/form-data" } : {}

        return axiosInstance.patch(url, formData, {
            headers: headers,
        })
    }

    return useHorizontalSigningMutation(
        url,
        axiosFn,
        {
            ...queryParameters,
        },
        {
            ...config,
            onSuccess: (data) => {
                queryClient.invalidateQueries({ queryKey: horizontalSigningQueryKeys.feature(signId) })
                invalidateDocumentProject(queryClient, selectedNetwork)
                invalidateDocumentsList(queryClient, selectedNetwork)
                config.onSuccess?.(data)
            },
        },
    )
}

export function useHorizontalSignUpdateCampaigns(
    signId,
    queryParameters,
    config = {},
) {
    const url = `horizontal_signs/${signId}/`
    const queryClient = useQueryClient()
    const { selectedNetwork } = useNetworksStateContext()

    return useHorizontalSigningMutation(
        url,
        axiosInstance.patch,
        {
            ...queryParameters,
        },
        {
            ...config,
            onSuccess: (data) => {
                queryClient.invalidateQueries({ queryKey: horizontalSigningQueryKeys.feature(signId) })
                queryClient.invalidateQueries({ queryKey: horizontalSigningQueryKeys["layer"]() })
                queryClient.invalidateQueries({ queryKey: horizontalSigningQueryKeys.stats_categories() })
                queryClient.invalidateQueries({ queryKey: horizontalSigningQueryKeys.table({ network: selectedNetwork.slug }) })
                invalidateCampaigns(queryClient, selectedNetwork)
                invalidateCampaignsList(queryClient, selectedNetwork)
                config.onSuccess?.(data)
            },
        },
    )
}